import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const VerificationRequirements = () => {
  const [requirements, setRequirements] = useState({
    identityVerification: true,
    driverLicenseRequired: true,
    insuranceRequired: true,
    backgroundCheckRequired: false,
    minimumAge: '21',
    creditScoreMinimum: '600',
    ownerCertificationRequired: true,
    vehicleRegistrationRequired: true,
    businessLicenseRequired: false,
    w9Required: true,
    autoVerifyIdentity: false,
    manualReviewThreshold: '3'
  });

  const [complianceStatus, setComplianceStatus] = useState({
    totalUsers: 1247,
    verified: 1089,
    pending: 142,
    rejected: 16
  });

  const handleInputChange = (field, value) => {
    setRequirements({ ...requirements, [field]: value });
  };

  const verificationLevelOptions = [
    { value: 'basic', label: 'Basic - Email + Phone' },
    { value: 'standard', label: 'Standard - ID + Address' },
    { value: 'enhanced', label: 'Enhanced - ID + Background Check' },
    { value: 'premium', label: 'Premium - Full KYC Verification' }
  ];

  const backgroundCheckProviders = [
    { value: 'checkr', label: 'Checkr' },
    { value: 'sterling', label: 'Sterling' },
    { value: 'goodhire', label: 'GoodHire' },
    { value: 'internal', label: 'Internal Review' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="ShieldCheck" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground">Verification Requirements</h2>
            <p className="font-body text-sm text-muted-foreground">Configure identity verification standards and compliance requirements</p>
          </div>
        </div>
      </div>

      {/* Compliance Overview */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <h3 className="font-heading text-base font-semibold text-foreground mb-4">Compliance Status Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-primary/10 rounded-md">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Users" size={16} className="text-primary" />
              <span className="font-body text-sm text-muted-foreground">Total Users</span>
            </div>
            <p className="font-heading text-2xl font-bold text-foreground">{complianceStatus?.totalUsers}</p>
          </div>
          <div className="p-4 bg-success/10 rounded-md">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="font-body text-sm text-muted-foreground">Verified</span>
            </div>
            <p className="font-heading text-2xl font-bold text-foreground">{complianceStatus?.verified}</p>
            <p className="font-caption text-xs text-muted-foreground mt-1">{((complianceStatus?.verified / complianceStatus?.totalUsers) * 100)?.toFixed(1)}% compliance</p>
          </div>
          <div className="p-4 bg-warning/10 rounded-md">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Clock" size={16} className="text-warning" />
              <span className="font-body text-sm text-muted-foreground">Pending</span>
            </div>
            <p className="font-heading text-2xl font-bold text-foreground">{complianceStatus?.pending}</p>
          </div>
          <div className="p-4 bg-error/10 rounded-md">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="XCircle" size={16} className="text-error" />
              <span className="font-body text-sm text-muted-foreground">Rejected</span>
            </div>
            <p className="font-heading text-2xl font-bold text-foreground">{complianceStatus?.rejected}</p>
          </div>
        </div>
      </div>

      {/* Identity Verification Standards */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-md mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="UserCheck" size={18} className="text-primary" strokeWidth={2} />
            <span>Identity Verification Standards</span>
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <Checkbox
                  label="Require Identity Verification"
                  description="Mandate government-issued ID verification"
                  checked={requirements?.identityVerification}
                  onChange={(e) => handleInputChange('identityVerification', e?.target?.checked)}
                />
                <Checkbox
                  label="Driver License Required"
                  description="Valid driver's license for all renters"
                  checked={requirements?.driverLicenseRequired}
                  onChange={(e) => handleInputChange('driverLicenseRequired', e?.target?.checked)}
                />
                <Checkbox
                  label="Auto-Verify Identity"
                  description="Use automated verification services"
                  checked={requirements?.autoVerifyIdentity}
                  onChange={(e) => handleInputChange('autoVerifyIdentity', e?.target?.checked)}
                />
              </div>
              <div className="space-y-3">
                <Input
                  label="Minimum Age Requirement"
                  type="number"
                  value={requirements?.minimumAge}
                  onChange={(e) => handleInputChange('minimumAge', e?.target?.value)}
                  description="Minimum age to rent trailers"
                  required
                />
                <Input
                  label="Manual Review Threshold"
                  type="number"
                  value={requirements?.manualReviewThreshold}
                  onChange={(e) => handleInputChange('manualReviewThreshold', e?.target?.value)}
                  description="Failed attempts before manual review"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Insurance Documentation */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-md mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="FileText" size={18} className="text-secondary" strokeWidth={2} />
            <span>Insurance Documentation Requirements</span>
          </h3>
          <div className="space-y-3">
            <Checkbox
              label="Require Insurance Verification"
              description="Renters must provide proof of insurance"
              checked={requirements?.insuranceRequired}
              onChange={(e) => handleInputChange('insuranceRequired', e?.target?.checked)}
            />
            <Checkbox
              label="Vehicle Registration Required"
              description="Owners must provide vehicle registration"
              checked={requirements?.vehicleRegistrationRequired}
              onChange={(e) => handleInputChange('vehicleRegistrationRequired', e?.target?.checked)}
            />
            <div className="mt-4 p-3 bg-muted rounded-md">
              <p className="font-body text-sm text-muted-foreground">
                <Icon name="Info" size={14} className="inline mr-1" />
                Insurance documents are automatically validated against state requirements
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Background Check Parameters */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-warning/5 border border-warning/20 rounded-md mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Search" size={18} className="text-warning" strokeWidth={2} />
            <span>Background Check Parameters</span>
          </h3>
          <div className="space-y-4">
            <Checkbox
              label="Require Background Checks"
              description="Mandatory background screening for all users"
              checked={requirements?.backgroundCheckRequired}
              onChange={(e) => handleInputChange('backgroundCheckRequired', e?.target?.checked)}
            />
            {requirements?.backgroundCheckRequired && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Select
                  label="Background Check Provider"
                  options={backgroundCheckProviders}
                  value="checkr"
                  onChange={() => {}}
                  description="Third-party verification service"
                  id="backgroundCheckProvider"
                  name="backgroundCheckProvider"
                  error=""
                />
                <Input
                  label="Minimum Credit Score"
                  type="number"
                  value={requirements?.creditScoreMinimum}
                  onChange={(e) => handleInputChange('creditScoreMinimum', e?.target?.value)}
                  description="Minimum acceptable credit score"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Owner Qualification Criteria */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-success/5 border border-success/20 rounded-md mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Award" size={18} className="text-success" strokeWidth={2} />
            <span>Owner Qualification Criteria</span>
          </h3>
          <div className="space-y-3">
            <Checkbox
              label="Owner Certification Required"
              description="Complete platform certification program"
              checked={requirements?.ownerCertificationRequired}
              onChange={(e) => handleInputChange('ownerCertificationRequired', e?.target?.checked)}
            />
            <Checkbox
              label="Business License Required"
              description="For commercial trailer operations"
              checked={requirements?.businessLicenseRequired}
              onChange={(e) => handleInputChange('businessLicenseRequired', e?.target?.checked)}
            />
            <Checkbox
              label="W-9 Tax Form Required"
              description="IRS W-9 for tax reporting compliance"
              checked={requirements?.w9Required}
              onChange={(e) => handleInputChange('w9Required', e?.target?.checked)}
            />
          </div>
        </div>
      </div>

      {/* Automated Compliance Checking */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-md">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Zap" size={18} className="text-primary" strokeWidth={2} />
            <span>Automated Compliance Checking</span>
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-card rounded-md border border-border">
              <div>
                <p className="font-body text-sm font-medium text-foreground">Real-time Document Validation</p>
                <p className="font-caption text-xs text-muted-foreground">Instant verification of uploaded documents</p>
              </div>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                <Icon name="Check" size={12} className="mr-1" />
                Active
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-card rounded-md border border-border">
              <div>
                <p className="font-body text-sm font-medium text-foreground">Expiration Date Monitoring</p>
                <p className="font-caption text-xs text-muted-foreground">Automatic alerts for expiring documents</p>
              </div>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                <Icon name="Check" size={12} className="mr-1" />
                Active
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-card rounded-md border border-border">
              <div>
                <p className="font-body text-sm font-medium text-foreground">Fraud Detection System</p>
                <p className="font-caption text-xs text-muted-foreground">AI-powered document authenticity checks</p>
              </div>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
                <Icon name="AlertCircle" size={12} className="mr-1" />
                Beta
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Shield" size={16} />
            <span>Verification changes apply to new registrations immediately</span>
          </div>
          <Button variant="outline" size="sm" iconName="Download">
            Export Compliance Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VerificationRequirements;